import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import {
  MagnifyingGlassIcon, ArrowDownTrayIcon, ChevronLeftIcon, ChevronRightIcon,
} from '@heroicons/react/24/outline'
import api from '../../services/api'

interface Purchase {
  id: number
  order_id: string
  payment_id: string | null
  amount: number
  currency: string
  status: string
  user_name: string | null
  user_email: string | null
  plan_name: string | null
  plan_type: string | null
  billing_period: string | null
  created_at: string | null
}

interface PlanOption {
  id: number
  name: string
  plan_type: string
  billing_period: string
}

const STATUS_META: Record<string, string> = {
  created: 'bg-blue-500/10 text-blue-400 border-blue-500/20',
  captured: 'bg-green-500/10 text-green-400 border-green-500/20',
  failed: 'bg-red-500/10 text-red-400 border-red-500/20',
  refunded: 'bg-orange-500/10 text-orange-400 border-orange-500/20',
}

const PAGE_SIZE = 20

export default function PurchasesPage() {
  const [purchases, setPurchases] = useState<Purchase[]>([])
  const [total, setTotal] = useState(0)
  const [loading, setLoading] = useState(true)
  const [exporting, setExporting] = useState(false)
  const [plans, setPlans] = useState<PlanOption[]>([])

  const [search, setSearch] = useState('')
  const [planFilter, setPlanFilter] = useState('')
  const [statusFilter, setStatusFilter] = useState('')
  const [dateFrom, setDateFrom] = useState('')
  const [dateTo, setDateTo] = useState('')
  const [page, setPage] = useState(1)

  useEffect(() => {
    api.get('/admin/plans').then(res => {
      setPlans(res.data.map((p: any) => ({ id: p.id, name: p.name, plan_type: p.plan_type, billing_period: p.billing_period })))
    }).catch(() => {})
  }, [])

  const buildParams = () => {
    const params: Record<string, any> = { skip: (page - 1) * PAGE_SIZE, limit: PAGE_SIZE }
    if (search) params.search = search
    if (planFilter) params.plan_id = planFilter
    if (statusFilter) params.status = statusFilter
    if (dateFrom) params.date_from = dateFrom
    if (dateTo) params.date_to = dateTo
    return params
  }

  const fetchPurchases = async () => {
    setLoading(true)
    try {
      const res = await api.get('/admin/purchases', { params: buildParams() })
      setPurchases(res.data.purchases)
      setTotal(res.data.total)
    } catch {}
    finally { setLoading(false) }
  }

  useEffect(() => { fetchPurchases() }, [search, planFilter, statusFilter, dateFrom, dateTo, page])
  useEffect(() => { setPage(1) }, [search, planFilter, statusFilter, dateFrom, dateTo])

  const handleExport = async () => {
    setExporting(true)
    try {
      const { skip, limit, ...filters } = buildParams()
      const res = await api.get('/admin/purchases/export', { params: filters, responseType: 'blob' })
      const url = window.URL.createObjectURL(new Blob([res.data]))
      const link = document.createElement('a')
      link.href = url
      link.setAttribute('download', 'purchases.csv')
      document.body.appendChild(link)
      link.click()
      link.remove()
    } catch {}
    finally { setExporting(false) }
  }

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE))

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <nav className="text-xs text-gray-400 mb-1"><Link to="/admin" className="hover:text-amber-400 transition-colors">RA Desk</Link> &rsaquo; Purchases</nav>
          <h1 className="text-2xl font-bold text-white">Purchases</h1>
          <p className="text-sm text-gray-400 mt-0.5">Every order attempt across every plan — created, captured, failed &amp; refunded</p>
        </div>
        <button onClick={handleExport} disabled={exporting}
          className="inline-flex items-center gap-2 px-3 py-2 rounded-xl border border-primary-600/40 bg-primary-500/10 text-primary-300 text-sm hover:bg-primary-500/20 transition-colors disabled:opacity-50">
          <ArrowDownTrayIcon style={{ width: 15, height: 15 }} />
          Export CSV
        </button>
      </div>

      {/* Filters */}
      <div className="bg-dark-800 rounded-2xl border border-dark-700 px-4 py-3 flex flex-wrap gap-3 items-center">
        <div className="relative flex-1 min-w-48">
          <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" style={{ width: 16, height: 16 }} />
          <input
            type="text"
            placeholder="Search user, order ID, transaction ID…"
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="w-full pl-9 pr-3 py-2 text-sm bg-dark-900 border border-dark-600 text-white placeholder-gray-500 rounded-lg focus:outline-none focus:ring-1 focus:ring-primary-500"
          />
        </div>
        <select value={planFilter} onChange={e => setPlanFilter(e.target.value)}
          className="text-sm bg-dark-900 border border-dark-600 text-white rounded-lg px-2 py-2 focus:outline-none focus:ring-1 focus:ring-primary-500">
          <option value="">All Plans</option>
          {plans.map(p => <option key={p.id} value={p.id}>{p.name} — {p.plan_type} · {p.billing_period}</option>)}
        </select>
        <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)}
          className="text-sm bg-dark-900 border border-dark-600 text-white rounded-lg px-2 py-2 focus:outline-none focus:ring-1 focus:ring-primary-500">
          <option value="">All Statuses</option>
          <option value="created">Created</option>
          <option value="captured">Captured</option>
          <option value="failed">Failed</option>
          <option value="refunded">Refunded</option>
        </select>
        <input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)}
          className="text-sm bg-dark-900 border border-dark-600 text-gray-200 rounded-lg px-2 py-2 focus:outline-none focus:ring-1 focus:ring-primary-500" />
        <span className="text-xs text-gray-500">to</span>
        <input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)}
          className="text-sm bg-dark-900 border border-dark-600 text-gray-200 rounded-lg px-2 py-2 focus:outline-none focus:ring-1 focus:ring-primary-500" />
      </div>

      {/* Table */}
      <div className="bg-dark-800 rounded-2xl border border-dark-700 overflow-hidden shadow-sm">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-dark-700 bg-dark-900 text-left text-xs text-gray-400 uppercase tracking-wider">
                <th className="px-4 py-3 font-semibold whitespace-nowrap">Date</th>
                <th className="px-4 py-3 font-semibold">User</th>
                <th className="px-4 py-3 font-semibold">Plan</th>
                <th className="px-4 py-3 font-semibold">Amount</th>
                <th className="px-4 py-3 font-semibold">Status</th>
                <th className="px-4 py-3 font-semibold">Order ID</th>
                <th className="px-4 py-3 font-semibold">Transaction ID</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-dark-700">
              {loading ? (
                <tr><td colSpan={7} className="text-center py-12 text-gray-400">Loading…</td></tr>
              ) : purchases.length === 0 ? (
                <tr><td colSpan={7} className="text-center py-12 text-gray-400">No purchases found</td></tr>
              ) : purchases.map(p => (
                <tr key={p.id} className="hover:bg-dark-700 transition-colors">
                  <td className="px-4 py-3 text-gray-300 whitespace-nowrap">
                    {p.created_at ? new Date(p.created_at).toLocaleDateString('en-IN') : '—'}
                  </td>
                  <td className="px-4 py-3">
                    <p className="text-gray-100">{p.user_name || '—'}</p>
                    <p className="text-xs text-gray-500">{p.user_email}</p>
                  </td>
                  <td className="px-4 py-3 text-xs text-gray-400">
                    {p.plan_name ? <>{p.plan_name} <span className="text-gray-600">({p.billing_period})</span></> : '—'}
                  </td>
                  <td className="px-4 py-3 text-gray-100 font-medium">₹{p.amount.toLocaleString('en-IN')}</td>
                  <td className="px-4 py-3">
                    <span className={`text-xs px-2 py-0.5 rounded-full border font-medium capitalize ${STATUS_META[p.status] || 'bg-dark-700 text-gray-400 border-dark-600'}`}>
                      {p.status}
                    </span>
                  </td>
                  <td className="px-4 py-3 font-mono text-xs text-gray-400">{p.order_id}</td>
                  <td className="px-4 py-3 font-mono text-xs text-gray-400">{p.payment_id || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {!loading && purchases.length > 0 && (
          <div className="flex items-center justify-between px-4 py-3 border-t border-dark-700">
            <p className="text-xs text-gray-400">
              Showing {(page - 1) * PAGE_SIZE + 1}–{Math.min(page * PAGE_SIZE, total)} of {total}
            </p>
            <div className="flex items-center gap-2">
              <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}
                className="w-8 h-8 rounded-lg border border-dark-600 bg-dark-900 flex items-center justify-center text-gray-300 hover:bg-dark-700 disabled:opacity-30 transition-colors">
                <ChevronLeftIcon style={{ width: 15, height: 15 }} />
              </button>
              <span className="text-xs text-gray-400">Page {page} of {totalPages}</span>
              <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages}
                className="w-8 h-8 rounded-lg border border-dark-600 bg-dark-900 flex items-center justify-center text-gray-300 hover:bg-dark-700 disabled:opacity-30 transition-colors">
                <ChevronRightIcon style={{ width: 15, height: 15 }} />
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
